import _slicedToArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { faChevronDown, faCopy, faPrint } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ActionIcon } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { hasValue, snakeToTitle } from "@/utils/format";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { MetaPill } from "./meta-pill";
import { processReport } from "./process-report";
var SECTION_CLS = "px-4 py-3 bg-sds-base-secondary border-t border-sds-base-default";

/** Expanded report content panel — renders report HTML, metadata pills, and a collapsible additional section. */
export function ReportVersionPanel(_ref) {
  var version = _ref.version,
    onPrint = _ref.onPrint,
    onCopy = _ref.onCopy;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(false),
    isAdditionalOpen = _useState[0],
    setIsAdditionalOpen = _useState[1];
  var metadata = version.metadata,
    additional = version.additional,
    reportHtml = version.reportHtml;
  var showActions = !!onPrint && !!onCopy;
  var hasMetadata = hasValue(metadata.acc) || hasValue(metadata.radiologists) || hasValue(metadata.referring) || hasValue(metadata.ordering) || hasValue(metadata.patientClass);
  var additionalEntries = Object.entries(additional);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "relative"
  }, /*#__PURE__*/React.createElement("div", {
    className: "px-3.5 pt-2.5 pb-2 max-h-[280px] overflow-y-auto scrollbar-sds".concat(showActions ? " pr-14" : "")
  }, /*#__PURE__*/React.createElement("div", {
    className: "text-left text-style-body-base text-sds-base-secondary break-words whitespace-pre-wrap",
    dangerouslySetInnerHTML: {
      __html: processReport(reportHtml)
    }
  })), showActions && /*#__PURE__*/React.createElement("div", {
    className: "absolute top-1.5 right-2 flex gap-0.5 z-10"
  }, /*#__PURE__*/React.createElement(ActionIcon, {
    "data-testid": "report-panel-print",
    variant: "default",
    size: 22,
    title: t("label.print", {
      defaultValue: "Print"
    }),
    "aria-label": t("label.print", {
      defaultValue: "Print"
    }),
    onClick: function onClick(e) {
      trackEvent(ANALYTICS_EVENTS.REPORT_PANEL_PRINT);
      e.stopPropagation();
      onPrint();
    }
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faPrint,
    className: "w-[10px] h-[10px]"
  })), /*#__PURE__*/React.createElement(ActionIcon, {
    "data-testid": "report-panel-copy",
    variant: "default",
    size: 22,
    title: t("label.copy", {
      defaultValue: "Copy"
    }),
    "aria-label": t("label.copy", {
      defaultValue: "Copy"
    }),
    onClick: function onClick(e) {
      trackEvent(ANALYTICS_EVENTS.REPORT_PANEL_COPY);
      e.stopPropagation();
      onCopy();
    }
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faCopy,
    className: "w-[10px] h-[10px]"
  })))), hasMetadata && /*#__PURE__*/React.createElement("div", {
    className: SECTION_CLS
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap gap-2"
  }, /*#__PURE__*/React.createElement(MetaPill, {
    label: t("label.acc", {
      defaultValue: "ACC"
    }),
    value: metadata.acc
  }), /*#__PURE__*/React.createElement(MetaPill, {
    label: t("label.radiologists", {
      defaultValue: "Radiologists"
    }),
    value: metadata.radiologists
  }), /*#__PURE__*/React.createElement(MetaPill, {
    label: t("label.referring", {
      defaultValue: "Referring"
    }),
    value: metadata.referring
  }), /*#__PURE__*/React.createElement(MetaPill, {
    label: t("label.ordering", {
      defaultValue: "Ordering"
    }),
    value: metadata.ordering
  }), /*#__PURE__*/React.createElement(MetaPill, {
    label: t("label.patient_class", {
      defaultValue: "Patient Class"
    }),
    value: metadata.patientClass
  }))), additionalEntries.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "bg-sds-base-secondary border-t border-sds-base-default"
  }, /*#__PURE__*/React.createElement(ActionIcon, {
    "data-testid": "report-panel-additional-toggle",
    variant: "transparent",
    className: "flex w-full items-center px-4 py-2 text-style-label-caps text-sds-base-tertiary hover:text-sds-brand-default transition-colors duration-150",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.REPORT_PANEL_ADDITIONAL_TOGGLE);
      setIsAdditionalOpen(function (prev) {
        return !prev;
      });
    },
    "aria-label": t("label.additional", {
      defaultValue: "Additional"
    })
  }, /*#__PURE__*/React.createElement("span", {
    className: "flex-1 text-left"
  }, t("label.additional", {
    defaultValue: "Additional"
  })), /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faChevronDown,
    className: "w-[11px] h-[11px] transition-transform duration-200 ease-in-out".concat(isAdditionalOpen ? " rotate-180" : "")
  })), /*#__PURE__*/React.createElement("div", {
    className: "grid transition-[grid-template-rows] duration-200 ease-in-out".concat(isAdditionalOpen ? " grid-rows-[1fr]" : " grid-rows-[0fr]")
  }, /*#__PURE__*/React.createElement("div", {
    className: "overflow-hidden min-h-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "px-4 pb-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap gap-2 pt-1"
  }, additionalEntries.map(function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
      key = _ref3[0],
      val = _ref3[1];
    return /*#__PURE__*/React.createElement(MetaPill, {
      key: key,
      label: snakeToTitle(key),
      value: val
    });
  })))))));
}